/**
 * TanStack Query hook for vault balance
 * 
 * Used to check total SC balance (wallet + vault) for purchase limit validation.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';

export interface VaultBalance {
  gc_vault_amount: number;
  sc_bonus_vault_amount: number;
  sc_promotional_vault_amount: number;
  sc_redeemable_vault_amount: number;
}

interface VaultBalanceResponse {
  wallet: {
    gc_amount: number;
    sc_bonus_amount: number;
    sc_promotional_amount: number;
    sc_redeemable_amount: number;
  };
  vault: VaultBalance;
}

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Fetch vault balance for the current user.
 * Used to calculate total SC balance (wallet + vault) for purchase limit checks.
 */
export function useVaultBalanceQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['vault-balance'],
    queryFn: async (): Promise<VaultBalance | null> => {
      const response = await apiClient.get<VaultBalanceResponse>('/vault/balance');

      if (response?.vault) {
        return response.vault;
      }
      return null;
    },
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES * 2,
    enabled: !!user,
  });
}
